/**
 * Страница трека: шаги по порядку, видно пройденное и следующее (FR-C7).
 *
 * Ссылка на задачу несёт `?track=<slug>`: по нему страница задачи понимает,
 * что пользователь идёт по треку, и предлагает следующий шаг трека, а не
 * следующую задачу темы (features/task/result/ExplanationCard.tsx).
 */

import { Link, useParams } from 'react-router'

import { useCurrentUser } from '@/api/auth'
import { useTrack, type TrackStep } from '@/api/tracks'
import { statusView } from '@/features/catalog/taskStatus'
import { strings } from '@/lib/strings'

import { TrackProgressBar } from './TrackProgressBar'

export function TrackPage() {
  const { slug = '' } = useParams()
  const { data: user } = useCurrentUser()
  const track = useTrack(slug)

  if (track.isPending) return <p className="text-sm text-muted">{strings.tracks.loading}</p>

  if (track.isError) {
    return (
      <section className="space-y-3">
        <p role="alert" className="text-sm text-warning">
          {strings.tracks.notFound}
        </p>
        <Link to="/tracks" className="text-sm text-accent underline">
          {strings.tracks.backToTracks}
        </Link>
      </section>
    )
  }

  const { data } = track
  const solved = data.tasks_solved
  const waitingReview = data.steps.filter((step) => step.progress?.needs_review === true).length

  return (
    <section className="space-y-6">
      <div>
        <Link to="/tracks" className="text-sm text-accent underline">
          {strings.tracks.backToTracks}
        </Link>
      </div>

      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">{data.title}</h1>
        {data.description !== null && <p className="text-sm text-muted">{data.description}</p>}
        <p className="text-xs text-muted">
          {solved === null
            ? strings.tracks.steps(data.tasks_total)
            : strings.tracks.stepsSolved(solved, data.tasks_total)}
        </p>
        {solved !== null && <TrackProgressBar solved={solved} total={data.tasks_total} />}
        {/* Трек при этом остаётся пройденным: долг не откатывает шаг и не
            мешает идти дальше (FR-P8). Это заметка, а не преграда. */}
        {waitingReview > 0 && (
          <p className="text-xs text-accent">{strings.review.stepsWaiting(waitingReview)}</p>
        )}
      </header>

      {data.next_task_slug !== null && (
        <p>
          <Link
            to={`/tasks/${data.next_task_slug}?track=${data.slug}`}
            className="inline-block rounded-lg bg-accent px-4 py-2 font-medium text-accent-fg"
          >
            {solved === 0 ? strings.tracks.start : strings.tracks.resume}
          </Link>
        </p>
      )}

      {solved !== null && data.next_task_slug === null && data.tasks_total > 0 && (
        <p className="text-sm text-success">{strings.tracks.finished}</p>
      )}

      {user == null && <p className="text-sm text-muted">{strings.tracks.loginToTrack}</p>}

      <ol className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface">
        {data.steps.map((step) => (
          <StepRow
            key={step.slug}
            step={step}
            trackSlug={data.slug}
            isNext={step.slug === data.next_task_slug}
          />
        ))}
      </ol>
    </section>
  )
}

function StepRow({
  step,
  trackSlug,
  isNext,
}: {
  step: TrackStep
  trackSlug: string
  isNext: boolean
}) {
  const status = statusView(step.progress)

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
      <span className="w-14 shrink-0 text-xs text-muted">
        {strings.tracks.stepNumber(step.position)}
      </span>

      <span className="flex items-center gap-1.5">
        <span aria-hidden="true" className={status.className}>
          {status.icon}
        </span>
        <span className="sr-only">{status.label}</span>
      </span>

      <Link to={`/tasks/${step.slug}?track=${trackSlug}`} className="font-medium hover:underline">
        {step.title}
      </Link>

      <span className="text-xs text-muted">{strings.catalog.difficulty[step.difficulty]}</span>
      <span className="text-xs text-muted">{step.topic.title}</span>

      {/* Отметка следующего шага текстом, а не только цветом рамки. */}
      {isNext && (
        <span className="ml-auto rounded border border-accent px-1.5 py-0.5 text-xs text-accent">
          {strings.tracks.nextStep}
        </span>
      )}
    </li>
  )
}
